'use client'

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User } from 'firebase/auth'
import { firebaseService } from '@/lib/firebase'

interface AuthContextType {
  user: User | null
  loading: boolean
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (email: string, password: string, displayName?: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithFacebook: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  signOut: () => Promise<void>
  error: string | null
  clearError: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Listen for auth state changes
    const unsubscribe = firebaseService.onAuthStateChanged((user) => {
      setUser(user)
      setLoading(false)
      setError(null) // Clear any previous errors
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const signInWithEmail = async (email: string, password: string) => {
    try {
      setError(null)
      setLoading(true)
      await firebaseService.signInWithEmail(email, password)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign in'
      setError(errorMessage)
      setLoading(false)
      throw error
    }
  }

  const signUpWithEmail = async (email: string, password: string, displayName?: string) => {
    try {
      setError(null)
      setLoading(true)
      await firebaseService.signUpWithEmail(email, password, displayName)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign up'
      setError(errorMessage)
      setLoading(false)
      throw error
    }
  }

  const signInWithGoogle = async () => {
    try {
      setError(null)
      setLoading(true)
      await firebaseService.signInWithGoogle()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign in with Google'
      setError(errorMessage)
      setLoading(false)
      throw error
    }
  }

  const signInWithFacebook = async () => {
    try {
      setError(null)
      setLoading(true)
      await firebaseService.signInWithFacebook()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign in with Facebook'
      setError(errorMessage)
      setLoading(false)
      throw error
    }
  }

  const resetPassword = async (email: string) => {
    try {
      setError(null)
      await firebaseService.resetPassword(email)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send password reset'
      setError(errorMessage)
      throw error
    }
  }

  const signOut = async () => {
    try {
      await firebaseService.signOut()
    } catch (error) {
      console.error('Sign out error:', error)
      setError('Failed to sign out')
    }
  }

  const clearError = () => setError(null)

  const value = {
    user,
    loading,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signInWithFacebook,
    resetPassword,
    signOut,
    error,
    clearError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
